import { Injectable } from "@angular/core";
import { Observable, of, shareReplay, Subject, switchMap } from "rxjs";
import { loadStripe } from "@stripe/stripe-js";
import { catchError, map, retry, take, tap, timeout } from "rxjs/operators";
import { StoreSelectorsService } from "./store-selectors.service";
import { HttpClient } from "@angular/common/http";
import { ActivatedRoute } from "@angular/router";
import { actionSelectedUserUpdate } from "../store/actions/user.actions";
import { IUser } from "../interfaces";
import { ToastrService } from "ngx-toastr";
import { DEFAULT_PORT, WEB_SERVER } from "../common/global-constants";
import _ from "lodash";


@Injectable({
    providedIn: "root",
})
export class FintechService {
    testKey = "pk_test_51PdWcODxKNLWTut5EIvnMa2VvDtG4hmBttOdzlhdIbcgBcBUJ0EBx31SADoK1qJmcyBLVQp6DOHPKx1JWvcBVv5K00yJwOOOFh";
    liveKey = "pk_live_51PdWcODxKNLWTut5fNR9i3anDzgt1zN3UjiBldVjHAhVHtP2xXmMWwSwLekhrzEADv3xL5wOfp4ialm7mpfYckh100H7KNYhUo";
    user: IUser;
    defaultFintech;
    businessId;
    payerId;
    resellerId;
    recurringMode;
    fintechInfoCache$: Observable<any>;
    payerToken$: Observable<any>;
    notify$: Subject<any>;
    busy$: Subject<any>;
    data;
    busy;
    paymentInfo;
    editMode = "info";
    stripe;
    /** set when getFintechInfo() could not deliver billing data (Fintree said "fail" or was unreachable); null when fine */
    error: string | null = null;
    /** payer token `data` was loaded with: a failed REFRESH for the same token keeps the loaded data on screen */
    private loadedForToken: string | null = null;
    /** the only token shape mars /dwSubscriberCardToken exchanges: <rc4 hex>.<expiry hex>.<hmac hex> (ms.verifyDurableCardToken) */
    private static readonly SIGNED_CARD_TOKEN = /^[0-9a-f]{20,200}\.[0-9a-f]{1,12}\.[0-9a-f]{32}$/i;

    // fintechInfo$;

    constructor(private storeSelector: StoreSelectorsService,
                private http: HttpClient,
                private route: ActivatedRoute,
                private toastr: ToastrService) {

        this.notify$ = new Subject<any>();
        this.busy$ = new Subject<any>();
    }

    private postToFintechWithRetry(endpoint: string, options: any): Observable<any> {
        const url = `https://pay.signage.me:8443/fintech/${endpoint}`;
        return this.http.post(url, options).pipe(
            retry({
                count: 5,
                delay: (error, retryCount) => {
                    this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, { timeOut: 3000 });
                    return of(error);
                },
            }),
        );
    }

    setBusy(busy) {
        this.busy = busy;
        this.busy$.next(busy);
    }

    getPayerToken(): Observable<any> {
        if (this.payerToken$ != null) {
            return this.payerToken$;
        }

        const payerToken = this.route.snapshot.paramMap.get("encrypted_id");
        if (payerToken != null) {
            this.payerToken$ = of(payerToken);
        } else {
            this.payerToken$ = of("token").pipe(
                switchMap(a => this.storeSelector.getUser()),
                take(1),
                switchMap(user => {
                    this.user = user;
                    const options = {
                        customerUser: user.email,
                        customerPass: user.passwordNew,
                    };
                    const url = "https://galaxy.signage.me/Web/getPayerToken.ashx";
                    return this.http.post(url, options);
                }),
                map(reply => (reply as any).token),
                shareReplay(1),
            );
        }
        return this.payerToken$;
    }


    /**
     * Token for /shopping-cart/subscriber/<token> links.
     * mars /dwSubscriberCardToken exchanges ONLY a signed durable card token (rc4.expiry.hmac, the /cardUpdate link
     * form) for a galaxy Fintree token. The links that normally reach this page carry either a galaxy token already
     * (the /cardUpdate redirect) or a legacy RC4 token, which mars refuses on purpose since 2026-09-07 - for both the
     * exchange could only ever answer {token: null}, so it is skipped: no cross-origin round trip, no 8 s stall when
     * mars is slow, and the URL token goes to Fintree as-is (a refused legacy token then surfaces as the
     * "ask support for a fresh link" message). A signed token pasted straight into this URL is still exchanged.
     */
    resolveUrlToken(urlToken: string): Observable<string> {
        if (!urlToken || !FintechService.SIGNED_CARD_TOKEN.test(urlToken)) return of(urlToken);
        const url = `${WEB_SERVER}:${DEFAULT_PORT}/dwSubscriberCardToken`;
        return this.http.post(url, { token: urlToken }).pipe(
            // a slow or unreachable mars must not recreate the "Loading..." hang: fall back to the URL token after 8 s
            timeout(8000),
            map((reply: any) => (reply && typeof reply.token === "string" && reply.token.length > 0) ? reply.token : urlToken),
            catchError(() => of(urlToken)),
            shareReplay(1),
        );
    }

    /**
     * Applies one getFintechInfo reply. `payerToken` is the token that reply was fetched with and `isCurrent` says
     * whether the pipeline delivering it is still the cached one - the pay-method editor always starts a second
     * fetch on init, so two pipelines for the same page run back to back and an older one must never overwrite or
     * evict the newer one's result.
     */
    private load(i_data, payerToken: string, isCurrent: () => boolean): Observable<any> {
        if (i_data == null || i_data.status == "fail") {
            // Fintree answered "fail" (bad / stale token, server-side error) or was unreachable after the retries.
            // Surface it: until 2026-09-07 this only reached console.error and the page stayed on "Loading..." forever.
            const reason = i_data && i_data.error ? String(i_data.error) : "empty reply from the payment server";
            console.error("fintech getFintechInfo failed: " + reason);
            if (!isCurrent()) {
                // an older pipeline lost the race against a newer one: the newer result stands
                return of(null);
            }
            this.fintechInfoCache$ = null; // never cache a failure: the next getFintechInfo() call tries again
            if (this.data != null && this.loadedForToken === payerToken) {
                // a REFRESH of billing data that is already on screen failed - keep showing it (the subscriber page
                // would otherwise tear down all billing sections and blame the link)
                this.toastr.warning("Could not refresh your billing information, showing the last loaded data. " + reason, "Billing", { timeOut: 10000 });
                return of(null);
            }
            this.error = reason;
            this.toastr.error("Could not load your billing information. " + reason, "Billing", { timeOut: 20000 });
            return of(null);
        }
        if (!isCurrent() && (this.data != null || this.fintechInfoCache$ != null)) {
            // a stale pipeline finished late while a newer one already delivered (data set) or is still running
            // (cache set): never let it overwrite the current data. With neither, the newer one failed and cleared
            // the cache, so this late success is the only billing data there is - keep it instead of a spinner
            return of(null);
        }
        this.error = null;
        this.data = i_data;
        this.loadedForToken = payerToken;
        this.defaultFintech = i_data.defaultFintech;
        this.businessId = i_data.businessId;
        this.payerId = i_data.payerId;
        this.resellerId = i_data.resellerId;
        this.recurringMode = i_data.recurringMode;


        const publishedKey = i_data.sandbox ? this.testKey : this.liveKey;
        if (this.stripe != null) {
            return of(this.stripe);
        } else {
            return new Observable(sub => {
                loadStripe(publishedKey).then(stripe => {
                    this.stripe = stripe;
                    sub.next(stripe);
                    sub.complete();
                }).catch(err => {
                    // js.stripe.com blocked (ad blocker, proxy, CSP): the pipeline used to never emit here, leaving the
                    // billing sections blank with no message and no way to retry
                    const reason = "the payment form could not be loaded (js.stripe.com is blocked on this network)";
                    console.error("fintech loadStripe failed: " + (err && err.message ? err.message : err));
                    if (isCurrent()) {
                        this.fintechInfoCache$ = null;
                        this.error = reason;
                        this.toastr.error("Could not load your billing information. " + reason, "Billing", { timeOut: 20000 });
                    }
                    sub.next(null);
                    sub.complete();
                });
            });
        }
    }

    getFintechInfo(): Observable<any> {
        if (this.fintechInfoCache$) {
            return this.fintechInfoCache$;
        }
        // a fresh attempt starts clean: this service is a root singleton, so a failure left behind by another page
        // (Account > Billing, checkout) must not drive the subscriber page's error banner while this fetch runs
        this.error = null;
        let pipeline$: Observable<any>;
        const isCurrent = () => this.fintechInfoCache$ === pipeline$;
        pipeline$ = this.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.postToFintechWithRetry("getFintechInfo", { token: payerToken }).pipe(
                    // retries exhausted (504 / network): hand load() a "fail" so the user sees an error, not "Loading..."
                    catchError(err => {
                        const reason = err && err.status ? `HTTP ${err.status}` : "network error";
                        return of({ status: "fail", error: `payment server unreachable (${reason})` });
                    }),
                    map(ret => ({ ret, payerToken })),
                );
            }),
            switchMap(({ ret, payerToken }) => this.load(ret, payerToken, isCurrent)),
            tap(a => {
                // listeners read this.data; a failure, a kept-on-screen refresh failure and a stale pipeline all emit null
                if (a != null && this.error == null) this.notify$.next("update");
            }),
            shareReplay(1),
        );
        this.fintechInfoCache$ = pipeline$;
        return pipeline$;
    }

    selectPayMethod(i_recurringPayMethodId, i_orderPayMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = {
                    token: payerToken,
                    recurringPayMethodId: i_recurringPayMethodId,
                    orderPayMethodId: i_orderPayMethodId,
                };
                return this.postToFintechWithRetry("selectPayMethod", options);
            }),
        );
    }

    deletePayMethod(i_payMethodId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, payMethodId: i_payMethodId };
                return this.postToFintechWithRetry("deletePayMethod", options);
            }),
        );
    }

    updateAccountStatus(i_status) {
        if (this.user != null) {
            const usr = _.cloneDeep(this.user);
            usr.accountStatusReseller = i_status;
            this.storeSelector.dispatch(actionSelectedUserUpdate({ user: usr }));
            this.toastr.clear();
        }
    }

    unsubscribe() {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                return this.postToFintechWithRetry("unsubscribe", { token: payerToken });
            }),
        );
    }

    getOrderInfo(i_orderId): Observable<any> {
        return this.getPayerToken().pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, orderId: i_orderId };
                return this.postToFintechWithRetry("getOrderInfo", options);
            }),
        );
    }

    createOrderInfo(i_orderId): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                const options = { token: payerToken, orderId: i_orderId };
                return this.postToFintechWithRetry("createOrderInfo", options);
            }),
        );
    }

    updateOrderInfo(orderInfo): Observable<any> {
        return this.payerToken$.pipe(
            switchMap(payerToken => {
                orderInfo.token = payerToken;
                return this.postToFintechWithRetry("updateOrderInfo", orderInfo);
            }),
        );
    }
}
